import { XIcon, EditIcon, TrashIcon, ClockIcon, FileIcon } from './Icons';
import { useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';

function initials(name = '') { return name.split(' ').slice(0, 2).map(n => n[0] || '').join('').toUpperCase(); }
function avatarGradient(name = '') {
  const g = ['linear-gradient(135deg,#667eea,#764ba2)', 'linear-gradient(135deg,#fb866d,#e8614a)', 'linear-gradient(135deg,#1a2744,#2d3f6b)', 'linear-gradient(135deg,#22c55e,#16a34a)', 'linear-gradient(135deg,#f59e0b,#d97706)', 'linear-gradient(135deg,#8b5cf6,#7c3aed)'];
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return g[Math.abs(h) % g.length];
}
function getAlert(emp) {
  if (!emp.iqamaExpiry) return null;
  const d = Math.ceil((new Date(emp.iqamaExpiry) - new Date()) / 86400000);
  if (d < 0) return { level: 'expired', days: d };
  if (d <= 30) return { level: 'critical', days: d };
  if (d <= 90) return { level: 'warning', days: d };
  return null;
}

function Field({ label, value, danger }) {
  return (
    <div>
      <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-[13.5px] break-all ${danger ? 'text-red-600 font-semibold' : 'text-slate-800'}`}>{value || '—'}</div>
    </div>
  );
}

const Section = ({ label }) => (
  <div className="sm:col-span-2 text-[11px] font-bold text-[#e8614a] uppercase tracking-widest mt-4 pb-2 border-b border-slate-100">{label}</div>
);

const alertBadge = {
  expired: 'bg-red-50 text-red-600 border-red-200',
  critical: 'bg-orange-50 text-[#e8614a] border-orange-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
};

async function loadLogoPng() {
  // /logo.webp → PNG data URL (jsPDF can't embed webp directly)
  try {
    const img = new Image();
    img.src = `${window.location.origin}/logo.webp`;
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const c = document.createElement('canvas');
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    c.getContext('2d').drawImage(img, 0, 0);
    return { data: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight };
  } catch { return null; }
}

async function downloadOfferLetter(emp) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 56; // margin
  const company = emp.sponsorship || 'Delta Hubb for Operations & Maintenance Co.';
  let y = 52;

  // header: logo + company
  const logo = await loadLogoPng();
  if (logo) {
    const h = 40, w = (logo.w / logo.h) * h;
    doc.addImage(logo.data, 'PNG', M, y - 8, w, h);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 27, 51);
    doc.text(company, M + w + 12, y + 8);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text('Jeddah, Kingdom of Saudi Arabia', M + w + 12, y + 22);
  } else {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 27, 51);
    doc.text(company, M, y + 8);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text('Jeddah, Kingdom of Saudi Arabia', M, y + 22);
  }
  y += 44;
  doc.setDrawColor(15, 27, 51); doc.setLineWidth(1.5);
  doc.line(M, y, W - M, y);
  y += 30;

  // centered title
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(15, 27, 51);
  doc.text('OFFER LETTER', W / 2, y, { align: 'center' });
  y += 26;

  // date right
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(30, 41, 59);
  doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, W - M, y, { align: 'right' });
  y += 28;

  doc.text(`Dear ${emp.gender === 'Female' ? 'Ms.' : 'Mr.'} ${emp.name},`, M, y);
  y += 20;

  const intro = `${company} is pleased to offer you employment. The details of this offer are outlined below. Please review the terms carefully and return a signed copy, along with the required documents, within three (3) working days.`;
  const introLines = doc.splitTextToSize(intro, W - 2 * M);
  doc.text(introLines, M, y);
  y += introLines.length * 14 + 16;

  // terms rows
  const rows = [
    ['Position:', emp.designation || '—'],
    ['Assigned Client / Deployment Site:', emp.project || '—'],
    ['Working Days:', "As per Assigned Client's schedule"],
    ['Annual Vacation:', '21 working days'],
    ['Expected Deployment Date:', emp.joiningDate || 'To be confirmed'],
    ['Contract Length:', '1 year'],
    ['Probationary Period:', '6 months'],
    ['Medical Insurance:', 'Provided as per company policy'],
    ['Total Package:', `SAR ${(emp.totalSalary || 0).toLocaleString()}`],
    ['Accommodation:', 'Included'],
    ['Transportation:', 'Included'],
    ['Tickets:', 'As per company policy'],
  ];
  rows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text(k, M, y);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59);
    doc.text(String(v), M + 210, y);
    y += 19;
  });
  y += 14;

  const clause = `The Iqama transfer process will be initiated upon receipt of official confirmation and approval from the assigned client, ${emp.project || ''}.`;
  doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 41, 59);
  const clauseLines = doc.splitTextToSize(clause, W - 2 * M);
  doc.text(clauseLines, M, y);
  y += clauseLines.length * 14 + 14;

  doc.text('Please sign below to confirm your acceptance of the offer detailed herein.', M, y);
  y += 44;

  doc.text('Name: ______________________________', M, y);
  y += 30;
  doc.text('Signature: ______________________________', M, y);

  doc.save(`Offer Letter - ${emp.name}.pdf`);
}

function downloadSettlementPDF(s) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 56;
  let y = 52;
  const emp = s.employee || s;
  const company = emp.sponsorship || 'Delta Hubb for Operations & Maintenance Co.';

  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 27, 51);
  doc.text(company, M, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
  doc.text('Jeddah, Kingdom of Saudi Arabia', M, y + 14);
  y += 30;
  doc.setDrawColor(15, 27, 51); doc.setLineWidth(1.5); doc.line(M, y, W - M, y);
  y += 28;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(15, 27, 51);
  doc.text('FINAL SETTLEMENT STATEMENT', W / 2, y, { align: 'center' });
  y += 30;

  doc.setFontSize(10.5);
  const info = [
    ['Employee', emp.name], ['Iqama No.', emp.iqamaNo || '-'],
    ['Designation', emp.designation || '-'], ['Project', emp.project || '-'],
    ['Joining Date', emp.joiningDate || '-'], ['Last Working Day', s.lastWorkingDay],
    ['Service', `${s.breakdown.serviceYears} years`], ['Monthly Salary', `SAR ${(emp.totalSalary || 0).toLocaleString()}`],
  ];
  info.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105); doc.text(k + ':', M, y);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59); doc.text(String(v), M + 150, y);
    y += 17;
  });
  y += 12;

  const b = s.breakdown;
  const rows = [
    [`Final month pay (${b.paidDays} paid days x SAR ${b.dayRate})`, b.finalMonthPay],
    ['Overtime (client-calculated)', b.otPay],
    [`End-of-service benefit (${b.serviceYears} yrs)${b.eosbComputed !== undefined ? ' — ADJUSTED' : ''}`, b.eosb],
    [`Leave encashment (${b.leaveDays} days)`, b.leavePay],
    ['Outstanding deductions cleared', -b.deductionClear],
  ];
  doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 27, 51);
  doc.text('Settlement Breakdown', M, y); y += 8;
  doc.setDrawColor(251, 134, 109); doc.line(M, y, W - M, y); y += 18;
  rows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 41, 59);
    doc.text(k, M, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(v < 0 ? 220 : 30, v < 0 ? 38 : 41, v < 0 ? 38 : 59);
    doc.text(`${v < 0 ? '- ' : ''}SAR ${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, W - M, y, { align: 'right' });
    y += 18;
  });
  y += 6;
  doc.setDrawColor(15, 27, 51); doc.setLineWidth(1); doc.line(M, y, W - M, y); y += 20;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12.5); doc.setTextColor(232, 97, 74);
  doc.text('NET SETTLEMENT', M, y);
  doc.text(`SAR ${s.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, W - M, y, { align: 'right' });
  y += 44;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(30, 41, 59);
  doc.text('I confirm receipt of my final settlement in full and have no further claims.', M, y);
  y += 40;
  doc.text('Employee Signature: ____________________________', M, y);
  y += 28;
  doc.text('Authorized Signatory: ____________________________', M, y);
  doc.save(`Final Settlement - ${emp.name}.pdf`);
}

async function fetchTimesheetHistory(empId) {
  const r = await api.get(`/employees/${empId}/timesheet-history`);
  return r.data;
}

async function downloadTimesheetExcel(empId) {
  const { employee, months } = await fetchTimesheetHistory(empId);
  if (!months.length) { alert('No timesheet records for this employee.'); return; }
  const wb = XLSX.utils.book_new();
  months.forEach(m => {
    const header = ['Day', ...Array.from({ length: m.daysInMonth }, (_, i) => i + 1)];
    const codes = ['Code', ...Array.from({ length: m.daysInMonth }, (_, i) => m.days[i + 1] || '')];
    const rows = [
      ['Employee', employee.name], ['Iqama No', employee.iqamaNo || ''], ['Designation', employee.designation || ''],
      ['Month', m.month], ['Projects', m.projects.join(', ')], [],
      header, codes, [],
      ['Summary'], ...Object.entries(m.counts).map(([k, v]) => [k, v]),
      ['Paid days', m.paidDays], ['OT (SAR)', m.otAmount],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 12 }, ...Array.from({ length: m.daysInMonth }, () => ({ wch: 4 }))];
    XLSX.utils.book_append_sheet(wb, ws, m.month);
  });
  XLSX.writeFile(wb, `Timesheet - ${employee.name}.xlsx`);
}

async function downloadTimesheetPDF(empId) {
  const { employee, months } = await fetchTimesheetHistory(empId);
  if (!months.length) { alert('No timesheet records for this employee.'); return; }
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  months.forEach((m, i) => {
    if (i > 0) doc.addPage();
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(15, 27, 51);
    doc.text(`Timesheet — ${employee.name}`, 40, 40);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(100, 116, 139);
    doc.text(`Iqama: ${employee.iqamaNo || '-'}   ·   ${employee.designation || '-'}   ·   Month: ${m.month}   ·   Projects: ${m.projects.join(', ')}${employee.terminationDate ? '   ·   Terminated: ' + employee.terminationDate : ''}`, 40, 56);
    const half = Math.ceil(m.daysInMonth / 2);
    const mk = (from, to) => ({
      head: [Array.from({ length: to - from + 1 }, (_, i) => String(from + i))],
      body: [Array.from({ length: to - from + 1 }, (_, i) => m.days[from + i] || '')],
    });
    const t1 = mk(1, half);
    autoTable(doc, {
      startY: 72, head: t1.head, body: t1.body, theme: 'grid',
      styles: { fontSize: 8.5, halign: 'center', cellPadding: 3 },
      headStyles: { fillColor: [15, 27, 51], fontSize: 8 }
    });
    const t2 = mk(half + 1, m.daysInMonth);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10, head: t2.head, body: t2.body, theme: 'grid',
      styles: { fontSize: 8.5, halign: 'center', cellPadding: 3 },
      headStyles: { fillColor: [15, 27, 51], fontSize: 8 }
    });
    const y = doc.lastAutoTable.finalY + 24;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(30, 41, 59);
    const summary = Object.entries(m.counts).map(([k, v]) => `${k}: ${v}`).join('    ');
    doc.text(`${summary}    Paid days: ${m.paidDays}    OT: SAR ${m.otAmount.toLocaleString()}`, 40, y);
  });
  doc.save(`Timesheet - ${employee.name}.pdf`);
}

function TerminateModal({ emp, onClose, onDone }) {
  const [lwd, setLwd] = useState('');
  const [eosbOverride, setEosbOverride] = useState('');
  const [leaveDaysOverride, setLeaveDaysOverride] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function preview() {
    if (!lwd) { setError('Pick the last working day first'); return; }
    setBusy(true); setError('');
    try {
      const r = await api.post(`/employees/${emp.id}/terminate`, { lastWorkingDay: lwd, preview: true, eosbOverride, leaveDaysOverride, overrideReason });
      setPreviewData(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  }

  async function commit() {
    setBusy(true); setError('');
    try {
      const r = await api.post(`/employees/${emp.id}/terminate`, { lastWorkingDay: lwd, eosbOverride, leaveDaysOverride, overrideReason });
      downloadSettlementPDF({ ...r.data.settlement, employee: r.data.settlement });
      onDone();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); setBusy(false); }
  }

  const b = previewData?.breakdown;
  const inputCls = "w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13.5px] outline-none focus:border-[#fb866d] transition-all";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,27,51,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && !busy && onClose()}>
      <div className="bg-white rounded-2xl w-[560px] max-w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800">Terminate &amp; Final Settlement</h2>
          <p className="text-xs text-slate-400 mt-0.5">{emp.name} · joined {emp.joiningDate} · SAR {(emp.totalSalary || 0).toLocaleString()}/month</p>
        </div>
        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Last Working Day *</label>
            <input type="date" className={inputCls} value={lwd} onChange={e => { setLwd(e.target.value); setPreviewData(null); }} />
          </div>
          {previewData && b && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 text-[13px] space-y-1.5">
              <div className="flex justify-between"><span>Final month ({b.paidDays} paid days x {b.dayRate})</span><b>SAR {b.finalMonthPay.toLocaleString()}</b></div>
              <div className="flex justify-between"><span>Overtime</span><b className="text-emerald-600">+ {b.otPay.toLocaleString()}</b></div>
              <div className="flex justify-between"><span>End-of-service ({b.serviceYears} yrs)</span><b className="text-emerald-600">+ {b.eosb.toLocaleString()}</b></div>
              <div className="flex justify-between"><span>Leave encashment ({b.leaveDays} days)</span><b className="text-emerald-600">+ {b.leavePay.toLocaleString()}</b></div>
              <div className="flex justify-between"><span>Deductions cleared in full</span><b className="text-red-500">- {b.deductionClear.toLocaleString()}</b></div>
              <div className="flex justify-between pt-2 border-t border-slate-200 text-[14px]"><span className="font-bold">NET SETTLEMENT</span><b className="text-[#e8614a]">SAR {previewData.net.toLocaleString()}</b></div>
            </div>
          )}
          {previewData && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">EOSB override (SAR)</label>
                <input type="number" className={inputCls} placeholder={`auto: ${b?.eosbComputed ?? b?.eosb ?? ''}`} value={eosbOverride} onChange={e => setEosbOverride(e.target.value)} />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Leave days override</label>
                <input type="number" className={inputCls} placeholder={`auto: ${b?.leaveDaysComputed ?? b?.leaveDays ?? ''}`} value={leaveDaysOverride} onChange={e => setLeaveDaysOverride(e.target.value)} />
              </div>
              {(eosbOverride || leaveDaysOverride) && (
                <div className="col-span-2">
                  <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Override reason</label>
                  <input className={inputCls} placeholder="e.g. resignation before 2 years — EOSB not due" value={overrideReason} onChange={e => setOverrideReason(e.target.value)} />
                </div>
              )}
            </div>
          )}
          {error && <div className="px-4 py-3 rounded-xl text-[13px] bg-red-50 border border-red-200 text-red-600">{error}</div>}
          <p className="text-[11.5px] text-slate-400">
            EOSB auto-calculated per Saudi labor law (half-month wage/year for first 5 years, full month after). Resignations
            may reduce or void EOSB — use the override with a reason. Committing freezes the settlement permanently, clears
            deduction balances in full, sets the employee to Terminated, and downloads the signed-ready statement PDF.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2.5 px-6 pb-5 pt-2 border-t border-slate-100">
          <button onClick={onClose} disabled={busy}
            className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all disabled:opacity-50">Cancel</button>
          {!previewData ? (
            <button onClick={preview} disabled={busy || !lwd}
              className="px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#0f1b33] hover:bg-[#1a2744] transition-all disabled:opacity-50">
              {busy ? 'Calculating...' : 'Calculate Settlement'}
            </button>
          ) : (
            <>
              <button onClick={preview} disabled={busy}
                className="px-4 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all disabled:opacity-50">Recalculate</button>
              <button onClick={commit} disabled={busy}
                className="px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-red-500 hover:bg-red-600 transition-all disabled:opacity-60">
                {busy ? 'Settling...' : 'Confirm & Terminate'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EmployeeDetail({ emp, onClose, onEdit, onDelete, onHistory }) {
  const { canEdit } = useAuth();
  const [showTerminate, setShowTerminate] = useState(false);
  const alert = getAlert(emp);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-fit max-w-full max-h-[90vh] flex flex-col shadow-2xl">

        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-base font-bold text-slate-800">Employee Profile</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
            <XIcon size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {/* Hero */}
          <div className="flex items-center gap-4 pb-5 border-b border-slate-100">
            <div className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center text-lg font-bold text-white"
              style={{ background: avatarGradient(emp.name) }}>
              {initials(emp.name)}
            </div>
            <div>
              <div className="text-[17px] font-bold text-slate-800 tracking-tight">{emp.name}</div>
              <div className="text-[13px] text-slate-400 mt-0.5">{emp.designation || 'No designation'} · {emp.project}</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${emp.status === 'Active' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-red-50 text-red-500 border-red-200'}`}>{emp.status}</span>
                {emp.iqamaStatus === 'Renewed' && <span className="px-3 py-1 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-600 border-emerald-200">Iqama Renewed</span>}
                {emp.iqamaStatus === 'Expired' && <span className="px-3 py-1 rounded-full text-[11px] font-semibold border bg-red-50 text-red-500 border-red-200">Iqama Expired</span>}
                {alert && <span className={`px-3 py-1 rounded-full text-[11px] font-bold border ${alertBadge[alert.level]}`}>{alert.days < 0 ? `Expired ${Math.abs(alert.days)}d ago` : `${alert.days}d left`}</span>}
              </div>
            </div>


          </div>


          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 flex-shrink-0">
            <div className="flex gap-2">
              {canEdit && (
                <button onClick={onDelete}
                  className="flex items-center gap-2 px-4 py-2.5 border border-red-200 rounded-full text-sm font-semibold text-red-500 hover:bg-red-50 hover:border-red-400 transition-all">
                  <TrashIcon size={14} /> Remove
                </button>
              )}
              <button onClick={onHistory}
                className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">
                <ClockIcon size={14} /> History
              </button>
              <button onClick={() => downloadTimesheetPDF(emp.id)}
                className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
                Timesheet PDF
              </button>
              <button onClick={() => downloadTimesheetExcel(emp.id)}
                className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
                Timesheet Excel
              </button>
              {emp.status === 'Active' && canEdit && (
                <button onClick={() => setShowTerminate(true)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-amber-300 rounded-full text-sm font-semibold text-amber-600 hover:bg-amber-50 transition-all">
                  Terminate &amp; Settle
                </button>
              )}
              <button onClick={() => downloadOfferLetter(emp)}
                className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
                <FileIcon size={14} /> Offer Letter
              </button>
            </div>
            <div className="flex gap-2.5">
              <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 rounded-full text-sm font-semibold text-slate-600 hover:border-slate-400 transition-all">Close</button>
              {canEdit && (
                <button onClick={onEdit}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all">
                  <EditIcon size={14} /> Edit
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Section label="Personal" />
            <Field label="Nationality" value={emp.nationality} />
            <Field label="Gender" value={emp.gender} />
            <Field label="Date of Birth" value={emp.dob} />
            <Field label="Contact" value={emp.contact} />

            <Section label="Employment" />
            <Field label="Joining Date" value={emp.joiningDate} />
            <Field label="Sponsorship" value={emp.sponsorship} />

            <Section label="Compliance" />
            <Field label="Iqama No." value={emp.iqamaNo} />
            <Field label="Iqama Expiry" value={emp.iqamaExpiry} danger={alert?.level === 'expired'} />
            <Field label="Qiwa Start" value={emp.qiwaStart} />
            <Field label="Qiwa Expiry" value={emp.qiwaEnd} />
            <Field label="Passport No." value={emp.passportNo} />
            <Field label="Passport Expiry" value={emp.passportExpiry} />
            <Field label="Health Insurance Expiry" value={emp.healthInsuranceExpiry} />

            <Section label="Compensation" />
            <Field label="Basic Salary" value={emp.basicSalary ? `SAR ${emp.basicSalary.toLocaleString()}` : null} />
            <Field label="Allowance" value={emp.allowance ? `SAR ${emp.allowance.toLocaleString()}` : null} />
            <Field label="Total Salary" value={emp.totalSalary ? `SAR ${emp.totalSalary.toLocaleString()}` : null} />
            <Field label="IBAN" value={emp.iban} />
            <Field label="Outstanding Deduction" value={emp.deductionBalance ? `SAR ${Number(emp.deductionBalance).toLocaleString()} ${Number(emp.monthlyInstallment) > 0 ? `(${Number(emp.monthlyInstallment).toLocaleString()}/month)` : '(no installment set — not being recovered)'}` : null} />
          </div>

          {emp.comments && (
            <div className="mt-5 p-4 bg-slate-50 border border-slate-100 rounded-xl text-[13px] text-slate-600">{emp.comments}</div>
          )}
        </div>


      </div>
      {showTerminate && (
        <TerminateModal emp={emp} onClose={() => setShowTerminate(false)}
          onDone={() => { setShowTerminate(false); onClose(); window.location.reload(); }} />
      )}
    </div>
  );
}